import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, BadgeCheck, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";

// "Listers who liked you": incoming right-swipes from listers/agents on THIS
// renter, from GET /api/swipes/liked-me. Each row shows the lister headshot,
// name, verified/realtor badge and the listing they most recently posted.
// Mutual matches are flagged and route to the chat. Honest empty state.
// Without Renter Plus the API answers {locked: true, count} and this page
// shows the count with a blurred teaser grid — never real data.
export default function LikedYouPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { markSeen } = useNotifications();
  const [rows, setRows] = useState(null);
  const [locked, setLocked] = useState(null); // {count} when Plus is required

  // Viewing this page consumes the "new likes" notification counter.
  useEffect(() => {
    markSeen("likes");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get("/swipes/liked-me")
      .then((res) => {
        if (!alive) return;
        if (res.data?.locked) {
          setLocked({ count: res.data.count || 0 });
          setRows([]);
          return;
        }
        setLocked(null);
        setRows(Array.isArray(res.data?.liked_me) ? res.data.liked_me : []);
      })
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [user?.id]);

  // Same navigation state shape as the swipe limit modal, so CheckoutPage
  // shows an honest summary even before the fallback product map loads.
  function goRenterPlus() {
    navigate("/checkout/renter_plus_monthly", {
      state: {
        item: {
          name: "SwipRent Renter Plus monthly",
          price: 9.99,
          desc: "See who liked you, unlimited swipes, unlimited rewinds and priority visibility.",
          recurring: "monthly",
        },
        checkout: { product: "renter_plus_monthly" },
      },
    });
  }

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-3xl px-4 pt-8 sm:px-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-night-text">
          <Heart size={20} className="fill-brand-primary text-brand-primary" /> Listers who liked you
        </h1>
        <p className="mt-1 text-sm text-night-sub">
          These listers swiped right on your profile. Like their home back to start a match.
        </p>

        {!rows ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : locked && locked.count > 0 ? (
          /* Renter Plus paywall: honest count + a blurred TEASER grid. The
             cards are generic placeholders, never real lister data. */
          <div className="mt-6">
            <div className="rounded-2xl border border-night-line bg-night-tint p-6 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-night-surface text-brand-primary">
                <Lock size={20} />
              </span>
              <h2 className="mt-3 text-lg font-semibold text-night-text">
                {locked.count} {locked.count === 1 ? "lister" : "listers"} liked your profile
              </h2>
              <p className="mx-auto mt-1 max-w-sm text-sm text-night-sub">
                Renter Plus shows you exactly who liked you, so you can like them back and match
                instantly. Matching and chatting are always free.
              </p>
              <button
                type="button"
                onClick={goRenterPlus}
                className="mt-4 rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
              >
                See who liked you
              </button>
              <p className="mt-2 text-xs text-night-sub">Renter Plus $9.99 per month</p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" aria-hidden="true">
              {Array.from({ length: Math.min(locked.count, 4) }).map((_, i) => (
                <div
                  key={i}
                  className="flex gap-4 overflow-hidden rounded-2xl border border-night-line bg-night-surface p-4 shadow-hairline"
                >
                  <div className="h-16 w-16 shrink-0 rounded-full bg-night-tint blur-sm" />
                  <div className="flex-1 space-y-2 py-1 blur-sm">
                    <div className="h-4 w-2/3 rounded bg-night-line" />
                    <div className="h-3 w-1/3 rounded bg-night-line" />
                    <div className="h-10 w-full rounded-xl bg-night-line" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <Heart className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No incoming likes yet</h2>
            <p className="mt-1 text-sm text-night-sub">
              Complete your profile and keep swiping. Listers who like you back show up here.
            </p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/profile")}>Polish your profile</ButtonGhost>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {rows.map((r) => (
              <div
                key={r.lister_id}
                className="flex gap-4 rounded-2xl border border-night-line bg-night-surface p-4 shadow-hairline"
              >
                <Link to={`/hosts/${r.lister_id}`} className="shrink-0">
                  {r.photo_url ? (
                    <img
                      src={r.photo_url}
                      alt={r.name}
                      className="h-16 w-16 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary text-xl font-semibold text-white">
                      {(r.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 font-semibold text-night-text">
                    <span className="truncate">{r.name || "A lister"}</span>
                    {r.verified && <BadgeCheck size={15} className="shrink-0 text-brand-primary" />}
                    {r.super && <Star size={13} className="shrink-0 fill-brand-primary text-brand-primary" />}
                  </p>
                  <p className="text-xs text-night-sub">
                    {r.is_realtor ? "Licensed realtor" : "Owner"}
                  </p>
                  {r.listing && (
                    <Link
                      to={`/listing/${r.listing.id}`}
                      className="mt-2 flex items-center gap-2 rounded-xl bg-night-tint p-2 transition hover:bg-night-line"
                    >
                      <img
                        src={r.listing.photos?.[0]}
                        alt={r.listing.title}
                        className="h-10 w-10 shrink-0 rounded-lg object-cover"
                        loading="lazy"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-night-text">{r.listing.title}</span>
                        <span className="block text-[11px] text-night-sub">
                          {r.listing.city}
                          {r.listing.price ? ` · $${Number(r.listing.price).toLocaleString()}/mo` : ""}
                        </span>
                      </span>
                    </Link>
                  )}
                  {r.matched ? (
                    <button
                      type="button"
                      onClick={() => navigate("/matches")}
                      className="mt-3 rounded-full bg-night-tint px-3 py-1.5 text-xs font-semibold text-brand-primary"
                    >
                      It is a match. Open chat
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(r.listing ? `/listing/${r.listing.id}` : "/swipe")}
                      className="mt-3 rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-hover"
                    >
                      View their home
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
